/**
 * AnnotationEmbankmentsBuilder.ts - renders concrete canal walls from
 * compiled/L3 embankment semantics as real geometry. This intentionally does not depend on
 * the terrain heightmap resolution: the wall is a smooth mesh extracted from
 * the painted embankment strip and drawn through StaticMeshPass.
 */

import { MESH_VERTEX_STRIDE, type MeshAsset } from "../assets/MeshAsset";
import {
  buildTerrainSurfaceMask,
  sampleTerrainHeight,
  sampleTerrainSurface,
  type TerrainAsset,
} from "../assets/TerrainAsset";
import type { Mat4, StaticMeshPass } from "../passes/StaticMeshPass";
import type { Aabb, Obb } from "./Collisions";
import { connectedComponents8 } from "./building-extrude";

/**
 * L3 reinforced-shore / embankment authored as a polyline along the water
 * edge. Slug coming from Nova may be `embankment` or `reinforced_shore`;
 * geometry type may be `line` or `polyline`. We accept any matching record
 * shape and ignore the rest.
 */
const EMBANKMENT_TYPE_SLUGS = new Set(["embankment", "reinforced_shore"]);
const EMBANKMENT_GEOMETRY_TYPES = new Set(["line", "polyline", "multi_line"]);

type Vec2 = [number, number];

interface EmbankmentSegment {
  p0: Vec2;
  p1: Vec2;
  waterNx: number;
  waterNz: number;
  length: number;
}

/**
 * Connected stretch of wall: front edge polyline along the waterline,
 * per-vertex unit normal pointing into the water, and per-vertex top/bottom
 * heights in world space. Welded into one strip when meshed.
 */
interface EmbankmentRibbon {
  points: Vec2[];
  waterN: Vec2[];
  topY: number[];
  bottomY: number[];
}

const GROUP_ID = "annotation.embankments.concrete";
const MIN_COMPONENT_PIXELS = 16;
const MIN_SEGMENT_LENGTH_M = 5.0;
const END_EXTEND_M = 1.0;
const FRONT_WATER_OFFSET_M = 0.25;
const EDGE_WATER_SEARCH_PX = 5;
const POLYLINE_STEP_M = 3.0;
const POLYLINE_SIMPLIFY_M = 0.7;
const TOP_WIDTH_M = 3.0;
const WALL_TOP_MIN_M = 0.45;
const WALL_TOP_MAX_M = 0.85;
const WALL_BOTTOM_M = -0.7;
const WATERLINE_BAND_HALF_M = 0.06;
// Probe terrain at multiple distances behind the wall and take the MAX so
// steep banks don't underestimate ground height and leave a tall exposed
// wall face. Distances in metres from the front edge inland.
const TERRAIN_PROBE_DISTANCES_M = [1.2, 2.4, 3.6];
// Running-window radius (in vertex count) used to smooth top heights along
// the ribbon. Without this each vertex follows local terrain noise and the
// wall coping becomes jagged.
const TOP_SMOOTH_RADIUS = 2;
// How far the authored polyline is pushed inland before meshing. The user
// typically draws the line along the visible shoreline, but the bank in 3D
// rises slightly behind that and a sliver of water bleeds through between
// the wall front face and the grass. Shifting the whole ribbon inland buries
// the wall foot in the bank and closes that gap.
const SHORE_BITE_M = 1.0;
// AABB-step along the ribbon is no longer used for collisions — we now
// emit OBBs (yaw-rotated boxes) that fit a diagonal wall snugly. Kept
// around in case a future code path wants to walk the ribbon in fixed
// world-space chunks.
const COLLIDER_STEP_M = 3.0;

// Painted concrete grey, slightly desaturated. Front (water side) reads as
// wet, top as dry capstone, back somewhere between. A thin dark band at the
// waterline anchors the wall in the water rather than floating on top of it.
const FRONT_COLOR: [number, number, number] = [0.38, 0.39, 0.37];
const TOP_COLOR: [number, number, number] = [0.5, 0.51, 0.48];
const SIDE_COLOR: [number, number, number] = [0.43, 0.44, 0.41];
const WATERLINE_COLOR: [number, number, number] = [0.22, 0.23, 0.21];

const IDENTITY: Mat4 = new Float32Array([
  1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1,
]) as Mat4;

export class AnnotationEmbankmentsBuilder {
  constructor(private readonly staticMeshPass: StaticMeshPass) {}

  build(
    terrain: TerrainAsset,
    waterLevel: number,
    staticAabbs?: Aabb[],
    staticObbs?: Obb[],
  ): void {
    const maskSegments = extractEmbankmentSegments(terrain);
    const objectRibbons = extractEmbankmentRibbonsFromObjects(
      terrain,
      waterLevel,
    );
    const maskRibbons = ribbonsFromSegments(terrain, waterLevel, maskSegments);
    const ribbons = [...maskRibbons, ...objectRibbons];
    if (ribbons.length === 0) return;
    const mesh = createEmbankmentMesh(ribbons, waterLevel);
    if (mesh.indices.length === 0) return;
    this.staticMeshPass.addGroup(GROUP_ID, mesh, 0, {
      castsShadow: true,
      castsHardShadow: true,
      receivesShadow: true,
    });
    this.staticMeshPass.addInstance(GROUP_ID, IDENTITY);
    // OBBs hug the wall direction; fall back to AABBs only if the caller
    // didn't pass an OBB sink (older code paths).
    if (staticObbs) {
      staticObbs.push(...createEmbankmentObbs(ribbons));
    } else {
      staticAabbs?.push(...createEmbankmentAabbs(ribbons));
    }
    console.log(
      `[Embankments] rendered ${ribbons.length} ribbon(s) (${maskRibbons.length} mask, ${objectRibbons.length} L3), ${Math.round(mesh.indices.length / 3)} tris`,
    );
  }
}

function ribbonsFromSegments(
  terrain: TerrainAsset,
  waterLevel: number,
  segs: EmbankmentSegment[],
): EmbankmentRibbon[] {
  const out: EmbankmentRibbon[] = [];
  for (const sg of segs) {
    const wN: Vec2 = [sg.waterNx, sg.waterNz];
    const points: Vec2[] = [sg.p0, sg.p1];
    const topY = points.map((p) => computeTopY(terrain, waterLevel, p, wN));
    out.push({
      points,
      waterN: [wN, wN],
      topY,
      bottomY: [waterLevel + WALL_BOTTOM_M, waterLevel + WALL_BOTTOM_M],
    });
  }
  return out;
}

function computeTopY(
  terrain: TerrainAsset,
  waterLevel: number,
  point: Vec2,
  waterN: Vec2,
): number {
  let inlandY = -Infinity;
  for (const d of TERRAIN_PROBE_DISTANCES_M) {
    const x = point[0] - waterN[0] * d;
    const z = point[1] - waterN[1] * d;
    const y = sampleTerrainHeight(terrain, x, z);
    if (Number.isFinite(y) && y > inlandY) inlandY = y;
  }
  if (!Number.isFinite(inlandY)) inlandY = waterLevel + WALL_TOP_MIN_M;
  // Sit the coping ~5 cm below the natural ground so the wall blends in with
  // the bank without poking through the grass.
  const topY = inlandY - 0.05;
  return Math.min(
    waterLevel + WALL_TOP_MAX_M,
    Math.max(waterLevel + WALL_TOP_MIN_M, topY),
  );
}

// Smooth a per-vertex top-height array with a symmetric box filter so the
// coping doesn't follow local terrain bumps. Endpoints are smoothed against
// the available neighbours only.
function smoothTopY(topY: number[]): number[] {
  const n = topY.length;
  if (n <= 2) return topY.slice();
  const r = TOP_SMOOTH_RADIUS;
  const out: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    let sum = 0;
    let count = 0;
    const lo = Math.max(0, i - r);
    const hi = Math.min(n - 1, i + r);
    for (let j = lo; j <= hi; j++) {
      sum += topY[j]!;
      count++;
    }
    out[i] = sum / count;
  }
  return out;
}

function createEmbankmentObbs(ribbons: EmbankmentRibbon[]): Obb[] {
  const boxes: Obb[] = [];
  // Wall front face sits slightly INTO the water (negative offset along
  // -waterN) and the back face sits TOP_WIDTH_M inland. The OBB local
  // frame: +X along the segment, +Z inland (away from water).
  const waterPaddingM = 0.25;
  const widthM = TOP_WIDTH_M + waterPaddingM;
  const halfWidth = widthM * 0.5;
  const inlandShift = (TOP_WIDTH_M - waterPaddingM) * 0.5;
  for (const ribbon of ribbons) {
    const pts = ribbon.points;
    for (let i = 0; i + 1 < pts.length; i++) {
      const a = pts[i]!;
      const b = pts[i + 1]!;
      const wA = ribbon.waterN[i]!;
      const wB = ribbon.waterN[i + 1]!;
      const wx = (wA[0] + wB[0]) * 0.5;
      const wz = (wA[1] + wB[1]) * 0.5;
      const wLen = Math.max(1e-6, Math.hypot(wx, wz));
      const wxN = wx / wLen;
      const wzN = wz / wLen;
      const dx = b[0] - a[0];
      const dz = b[1] - a[1];
      const len = Math.hypot(dx, dz);
      if (len < 1e-6) continue;
      const halfLen = len * 0.5;
      const midX = (a[0] + b[0]) * 0.5 - wxN * inlandShift;
      const midZ = (a[1] + b[1]) * 0.5 - wzN * inlandShift;
      const yBottom = Math.min(ribbon.bottomY[i]!, ribbon.bottomY[i + 1]!);
      const yTop = Math.max(ribbon.topY[i]!, ribbon.topY[i + 1]!);
      const cy = (yBottom + yTop) * 0.5;
      const hy = (yTop - yBottom) * 0.5;
      const rotY = Math.atan2(dz, dx);
      boxes.push({
        center: [midX, cy, midZ],
        halfExtents: [halfLen, hy, halfWidth],
        rotY,
        kind: "embankment",
      });
    }
  }
  return boxes;
}

function createEmbankmentAabbs(ribbons: EmbankmentRibbon[]): Aabb[] {
  const boxes: Aabb[] = [];
  const waterPaddingM = 0.25;
  for (const ribbon of ribbons) {
    const pts = ribbon.points;
    for (let i = 0; i + 1 < pts.length; i++) {
      const a = pts[i]!;
      const b = pts[i + 1]!;
      const wA = ribbon.waterN[i]!;
      const wB = ribbon.waterN[i + 1]!;
      const wx = (wA[0] + wB[0]) * 0.5;
      const wz = (wA[1] + wB[1]) * 0.5;
      const wLen = Math.max(1e-6, Math.hypot(wx, wz));
      const wxN = wx / wLen;
      const wzN = wz / wLen;
      const lx = -wxN;
      const lz = -wzN;
      const dx = b[0] - a[0];
      const dz = b[1] - a[1];
      const len = Math.hypot(dx, dz);
      const steps = Math.max(1, Math.ceil(len / COLLIDER_STEP_M));
      for (let step = 0; step < steps; step++) {
        const t0 = step / steps;
        const t1 = (step + 1) / steps;
        const p0: Vec2 = [a[0] + dx * t0, a[1] + dz * t0];
        const p1: Vec2 = [a[0] + dx * t1, a[1] + dz * t1];
        const corners: Vec2[] = [
          [p0[0] + wxN * waterPaddingM, p0[1] + wzN * waterPaddingM],
          [p1[0] + wxN * waterPaddingM, p1[1] + wzN * waterPaddingM],
          [p0[0] + lx * TOP_WIDTH_M, p0[1] + lz * TOP_WIDTH_M],
          [p1[0] + lx * TOP_WIDTH_M, p1[1] + lz * TOP_WIDTH_M],
        ];
        let minX = Infinity;
        let maxX = -Infinity;
        let minZ = Infinity;
        let maxZ = -Infinity;
        for (const [x, z] of corners) {
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          minZ = Math.min(minZ, z);
          maxZ = Math.max(maxZ, z);
        }
        const yBottom = Math.min(ribbon.bottomY[i]!, ribbon.bottomY[i + 1]!);
        const yTop = Math.max(ribbon.topY[i]!, ribbon.topY[i + 1]!);
        boxes.push({
          min: [minX, yBottom, minZ],
          max: [maxX, yTop, maxZ],
          kind: "embankment",
        });
      }
    }
  }
  return boxes;
}

function extractEmbankmentSegments(terrain: TerrainAsset): EmbankmentSegment[] {
  const embankmentMask = buildTerrainSurfaceMask(terrain, "embankment");
  const waterMask = buildTerrainSurfaceMask(terrain, "water");
  if (
    !embankmentMask ||
    !waterMask ||
    embankmentMask.width !== waterMask.width ||
    embankmentMask.height !== waterMask.height
  ) {
    return [];
  }
  const aw = embankmentMask.width;
  const ah = embankmentMask.height;
  const embankment = embankmentMask.mask;
  const water = waterMask.mask;

  const wM = terrain.worldWidthM;
  const hM = terrain.worldHeightM;
  const halfW = wM * 0.5;
  const halfH = hM * 0.5;
  const pxToWorldX = wM / aw;
  const pxToWorldZ = hM / ah;
  const toWorld = (x: number, y: number): Vec2 => [
    -halfW + (x + 0.5) * pxToWorldX,
    -halfH + (y + 0.5) * pxToWorldZ,
  ];

  const waterFacingEdge = new Uint8Array(aw * ah);
  for (let y = 0; y < ah; y++) {
    for (let x = 0; x < aw; x++) {
      const idx = y * aw + x;
      if (!embankment[idx]) continue;
      if (hasNearbyWater(water, aw, ah, x, y, EDGE_WATER_SEARCH_PX)) {
        waterFacingEdge[idx] = 1;
      }
    }
  }

  const segments: EmbankmentSegment[] = [];
  for (const comp of connectedComponents8(embankment, aw, ah)) {
    if (comp.length < MIN_COMPONENT_PIXELS) continue;

    let cx = 0;
    let cz = 0;
    const worldPts: Vec2[] = [];
    for (const [x, y] of comp) {
      const p = toWorld(x, y);
      worldPts.push(p);
      cx += p[0];
      cz += p[1];
    }
    cx /= worldPts.length;
    cz /= worldPts.length;

    let cxx = 0;
    let czz = 0;
    let cxz = 0;
    for (const [x, z] of worldPts) {
      const dx = x - cx;
      const dz = z - cz;
      cxx += dx * dx;
      czz += dz * dz;
      cxz += dx * dz;
    }
    const yaw = 0.5 * Math.atan2(2 * cxz, cxx - czz);
    const ux = Math.cos(yaw);
    const uz = Math.sin(yaw);
    const nx = -uz;
    const nz = ux;

    let minS = Infinity;
    let maxS = -Infinity;
    let minQ = Infinity;
    let maxQ = -Infinity;
    for (const [x, z] of worldPts) {
      const dx = x - cx;
      const dz = z - cz;
      const s = dx * ux + dz * uz;
      const q = dx * nx + dz * nz;
      minS = Math.min(minS, s);
      maxS = Math.max(maxS, s);
      minQ = Math.min(minQ, q);
      maxQ = Math.max(maxQ, q);
    }
    const length = maxS - minS;
    if (length < MIN_SEGMENT_LENGTH_M) continue;

    const waterSign = detectWaterSide(
      comp,
      water,
      aw,
      ah,
      toWorld,
      cx,
      cz,
      nx,
      nz,
    );
    const waterNx = nx * waterSign;
    const waterNz = nz * waterSign;
    const edgePts: Array<{ s: number; q: number }> = [];
    for (const [x, y] of comp) {
      if (!waterFacingEdge[y * aw + x]) continue;
      const [wx, wz] = toWorld(x, y);
      const dx = wx - cx;
      const dz = wz - cz;
      edgePts.push({ s: dx * ux + dz * uz, q: dx * nx + dz * nz });
    }
    if (edgePts.length < 2) {
      for (const [x, y] of comp) {
        const [wx, wz] = toWorld(x, y);
        const dx = wx - cx;
        const dz = wz - cz;
        edgePts.push({ s: dx * ux + dz * uz, q: dx * nx + dz * nz });
      }
    }

    const polyline = buildWaterEdgePolyline(
      edgePts,
      cx,
      cz,
      ux,
      uz,
      nx,
      nz,
      waterSign,
    );
    for (let i = 0; i + 1 < polyline.length; i++) {
      let p0 = polyline[i]!;
      let p1 = polyline[i + 1]!;
      const segDx = p1[0] - p0[0];
      const segDz = p1[1] - p0[1];
      const segLen = Math.hypot(segDx, segDz);
      if (segLen < 0.25) continue;
      if (i === 0) {
        p0 = [
          p0[0] - (segDx / segLen) * END_EXTEND_M,
          p0[1] - (segDz / segLen) * END_EXTEND_M,
        ];
      }
      if (i === polyline.length - 2) {
        p1 = [
          p1[0] + (segDx / segLen) * END_EXTEND_M,
          p1[1] + (segDz / segLen) * END_EXTEND_M,
        ];
      }
      segments.push({ p0, p1, waterNx, waterNz, length: segLen });
    }
  }
  return segments;
}

function extractEmbankmentRibbonsFromObjects(
  terrain: TerrainAsset,
  waterLevel: number,
): EmbankmentRibbon[] {
  const ribbons: EmbankmentRibbon[] = [];
  for (const raw of terrain.worldObjects ?? []) {
    const obj = raw as unknown as {
      type?: unknown;
      geometry?: {
        type?: unknown;
        points?: unknown;
        lines?: unknown;
      };
    };
    const typeSlug = typeof obj.type === "string" ? obj.type : "";
    if (!EMBANKMENT_TYPE_SLUGS.has(typeSlug)) continue;
    const geom = obj.geometry;
    if (!geom || typeof geom !== "object") continue;
    const geomType = typeof geom.type === "string" ? geom.type : "";
    if (!EMBANKMENT_GEOMETRY_TYPES.has(geomType)) continue;

    const lines: Vec2[][] = [];
    if (geomType === "multi_line" && Array.isArray(geom.lines)) {
      for (const ln of geom.lines) {
        const pts = coercePolylinePoints(ln);
        if (pts.length >= 2) lines.push(pts);
      }
    } else if (Array.isArray(geom.points)) {
      const pts = coercePolylinePoints(geom.points);
      if (pts.length >= 2) lines.push(pts);
    }

    for (const pts of lines) {
      const ribbon = ribbonFromPolyline(terrain, waterLevel, pts);
      if (ribbon) ribbons.push(ribbon);
    }
  }
  return ribbons;
}

function coercePolylinePoints(raw: unknown): Vec2[] {
  if (!Array.isArray(raw)) return [];
  const pts: Vec2[] = [];
  for (const p of raw) {
    if (!Array.isArray(p) || p.length < 2) continue;
    const x = Number(p[0]);
    const z = Number(p[1]);
    if (!Number.isFinite(x) || !Number.isFinite(z)) continue;
    pts.push([x, z]);
  }
  return pts;
}

function ribbonFromPolyline(
  terrain: TerrainAsset,
  waterLevel: number,
  pts: Vec2[],
): EmbankmentRibbon | null {
  if (pts.length < 2) return null;
  const probeDistances = [2.0, 4.0, 8.0];

  // Per-segment unit normal pointing into water.
  const segNormals: Vec2[] = [];
  let waterFlipVotes = 0;
  let waterStayVotes = 0;
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const dx = b[0] - a[0];
    const dz = b[1] - a[1];
    const len = Math.hypot(dx, dz);
    if (len < 1e-3) {
      segNormals.push([0, 0]);
      continue;
    }
    const ux = dx / len;
    const uz = dz / len;
    const nx = -uz;
    const nz = ux;
    const mx = (a[0] + b[0]) * 0.5;
    const mz = (a[1] + b[1]) * 0.5;
    let leftWater = 0;
    let rightWater = 0;
    for (const d of probeDistances) {
      if (sampleTerrainSurface(terrain, mx + nx * d, mz + nz * d) === "water")
        leftWater++;
      if (sampleTerrainSurface(terrain, mx - nx * d, mz - nz * d) === "water")
        rightWater++;
    }
    let sign: number;
    if (leftWater === rightWater) {
      sign = waterFlipVotes >= waterStayVotes ? -1 : 1;
    } else {
      sign = leftWater > rightWater ? 1 : -1;
    }
    if (sign > 0) waterStayVotes++;
    else waterFlipVotes++;
    segNormals.push([nx * sign, nz * sign]);
  }

  // Per-vertex normal = average of adjacent segment normals; renormalise.
  const vertNormals: Vec2[] = [];
  for (let i = 0; i < pts.length; i++) {
    const prev = i > 0 ? segNormals[i - 1] : undefined;
    const next = i < segNormals.length ? segNormals[i] : undefined;
    let nx = 0;
    let nz = 0;
    if (prev) {
      nx += prev[0];
      nz += prev[1];
    }
    if (next) {
      nx += next[0];
      nz += next[1];
    }
    const m = Math.hypot(nx, nz);
    if (m > 1e-4) {
      vertNormals.push([nx / m, nz / m]);
    } else {
      vertNormals.push([
        next?.[0] ?? prev?.[0] ?? 1,
        next?.[1] ?? prev?.[1] ?? 0,
      ]);
    }
  }

  // Push the front edge slightly inland (opposite the water normal) so the
  // wall foot is embedded in the bank instead of standing in front of it.
  const shifted: Vec2[] = pts.map((p, i) => {
    const n = vertNormals[i]!;
    return [p[0] - n[0] * SHORE_BITE_M, p[1] - n[1] * SHORE_BITE_M];
  });

  const rawTopY = shifted.map((p, i) =>
    computeTopY(terrain, waterLevel, p, vertNormals[i]!),
  );
  const topY = smoothTopY(rawTopY);
  const bottomY = shifted.map(() => waterLevel + WALL_BOTTOM_M);

  return {
    points: shifted,
    waterN: vertNormals,
    topY,
    bottomY,
  };
}

function hasNearbyWater(
  water: Uint8Array,
  aw: number,
  ah: number,
  x: number,
  y: number,
  radiusPx: number,
): boolean {
  for (let dy = -radiusPx; dy <= radiusPx; dy++) {
    const yy = y + dy;
    if (yy < 0 || yy >= ah) continue;
    for (let dx = -radiusPx; dx <= radiusPx; dx++) {
      const xx = x + dx;
      if (xx < 0 || xx >= aw) continue;
      if (water[yy * aw + xx]) return true;
    }
  }
  return false;
}

function buildWaterEdgePolyline(
  edgePts: Array<{ s: number; q: number }>,
  cx: number,
  cz: number,
  ux: number,
  uz: number,
  nx: number,
  nz: number,
  waterSign: number,
): Vec2[] {
  if (edgePts.length < 2) return [];
  let minS = Infinity;
  let maxS = -Infinity;
  for (const p of edgePts) {
    minS = Math.min(minS, p.s);
    maxS = Math.max(maxS, p.s);
  }
  const binCount = Math.max(2, Math.ceil((maxS - minS) / POLYLINE_STEP_M));
  const bins: Array<{ sSum: number; q: number; count: number }> = Array.from(
    { length: binCount },
    () => ({ sSum: 0, q: waterSign > 0 ? -Infinity : Infinity, count: 0 }),
  );
  for (const p of edgePts) {
    const t = (p.s - minS) / Math.max(1e-6, maxS - minS);
    const bi = Math.max(0, Math.min(binCount - 1, Math.floor(t * binCount)));
    const bin = bins[bi]!;
    bin.sSum += p.s;
    bin.q = waterSign > 0 ? Math.max(bin.q, p.q) : Math.min(bin.q, p.q);
    bin.count++;
  }

  const raw: Vec2[] = [];
  for (const bin of bins) {
    if (bin.count === 0) continue;
    const s = bin.sSum / bin.count;
    const q = bin.q + waterSign * FRONT_WATER_OFFSET_M;
    raw.push([cx + ux * s + nx * q, cz + uz * s + nz * q]);
  }
  return douglasPeucker(raw, POLYLINE_SIMPLIFY_M);
}

function douglasPeucker(pts: Vec2[], tol: number): Vec2[] {
  if (pts.length < 3) return pts.slice();
  const keep = new Uint8Array(pts.length);
  keep[0] = 1;
  keep[pts.length - 1] = 1;
  const stack: Array<[number, number]> = [[0, pts.length - 1]];
  const tol2 = tol * tol;
  while (stack.length) {
    const [a, b] = stack.pop()!;
    if (b <= a + 1) continue;
    const [ax, az] = pts[a]!;
    const [bx, bz] = pts[b]!;
    const dx = bx - ax;
    const dz = bz - az;
    const segLen2 = dx * dx + dz * dz;
    let maxD2 = 0;
    let maxI = -1;
    for (let i = a + 1; i < b; i++) {
      const [px, pz] = pts[i]!;
      let d2: number;
      if (segLen2 < 1e-9) {
        const ex = px - ax;
        const ez = pz - az;
        d2 = ex * ex + ez * ez;
      } else {
        const t = Math.max(
          0,
          Math.min(1, ((px - ax) * dx + (pz - az) * dz) / segLen2),
        );
        const cx = ax + dx * t;
        const cz = az + dz * t;
        const ex = px - cx;
        const ez = pz - cz;
        d2 = ex * ex + ez * ez;
      }
      if (d2 > maxD2) {
        maxD2 = d2;
        maxI = i;
      }
    }
    if (maxI >= 0 && maxD2 > tol2) {
      keep[maxI] = 1;
      stack.push([a, maxI], [maxI, b]);
    }
  }
  const out: Vec2[] = [];
  for (let i = 0; i < pts.length; i++) if (keep[i]) out.push(pts[i]!);
  return out;
}

function detectWaterSide(
  comp: Array<[number, number]>,
  water: Uint8Array,
  aw: number,
  ah: number,
  toWorld: (x: number, y: number) => Vec2,
  cx: number,
  cz: number,
  nx: number,
  nz: number,
): number {
  let positive = 0;
  let negative = 0;
  const radiusPx = 6;
  for (const [x, y] of comp) {
    for (let dy = -radiusPx; dy <= radiusPx; dy++) {
      const yy = y + dy;
      if (yy < 0 || yy >= ah) continue;
      for (let dx = -radiusPx; dx <= radiusPx; dx++) {
        const xx = x + dx;
        if (xx < 0 || xx >= aw) continue;
        if (!water[yy * aw + xx]) continue;
        const [wx, wz] = toWorld(xx, yy);
        const q = (wx - cx) * nx + (wz - cz) * nz;
        if (q >= 0) positive++;
        else negative++;
      }
    }
  }
  return positive >= negative ? 1 : -1;
}

function createEmbankmentMesh(
  ribbons: EmbankmentRibbon[],
  waterLevel: number,
): MeshAsset {
  const verts: number[] = [];
  const idx: number[] = [];
  const pushVertex = (
    x: number,
    y: number,
    z: number,
    nx: number,
    ny: number,
    nz: number,
    color: [number, number, number],
  ): number => {
    const vi = verts.length / 9;
    verts.push(x, y, z, nx, ny, nz, color[0], color[1], color[2]);
    return vi;
  };
  const addQuad = (
    a: [number, number, number],
    b: [number, number, number],
    c: [number, number, number],
    d: [number, number, number],
    na: [number, number, number],
    nb: [number, number, number],
    nc: [number, number, number],
    nd: [number, number, number],
    color: [number, number, number],
  ): void => {
    const base = verts.length / 9;
    pushVertex(a[0], a[1], a[2], na[0], na[1], na[2], color);
    pushVertex(b[0], b[1], b[2], nb[0], nb[1], nb[2], color);
    pushVertex(c[0], c[1], c[2], nc[0], nc[1], nc[2], color);
    pushVertex(d[0], d[1], d[2], nd[0], nd[1], nd[2], color);
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };

  for (const ribbon of ribbons) {
    const pts = ribbon.points;
    const wn = ribbon.waterN;
    const topY = ribbon.topY;
    const botY = ribbon.bottomY;
    const n = pts.length;
    if (n < 2) continue;

    // Precompute per-vertex back (inland) point.
    const backXZ: Vec2[] = pts.map((p, i) => [
      p[0] - wn[i]![0] * TOP_WIDTH_M,
      p[1] - wn[i]![1] * TOP_WIDTH_M,
    ]);
    const inlandN: Vec2[] = wn.map(([x, z]) => [-x, -z]);

    for (let i = 0; i + 1 < n; i++) {
      const a = pts[i]!;
      const b = pts[i + 1]!;
      const wA = wn[i]!;
      const wB = wn[i + 1]!;
      const iA = inlandN[i]!;
      const iB = inlandN[i + 1]!;
      const yT0 = topY[i]!;
      const yT1 = topY[i + 1]!;
      const yB0 = botY[i]!;
      const yB1 = botY[i + 1]!;

      const fa = [a[0], yT0, a[1]] as [number, number, number];
      const fb = [b[0], yT1, b[1]] as [number, number, number];
      const fab = [a[0], yB0, a[1]] as [number, number, number];
      const fbb = [b[0], yB1, b[1]] as [number, number, number];

      const bA = backXZ[i]!;
      const bB = backXZ[i + 1]!;
      const ba = [bA[0], yT0, bA[1]] as [number, number, number];
      const bb = [bB[0], yT1, bB[1]] as [number, number, number];
      const bab = [bA[0], yB0, bA[1]] as [number, number, number];
      const bbb = [bB[0], yB1, bB[1]] as [number, number, number];

      const nFA: [number, number, number] = [wA[0], 0, wA[1]];
      const nFB: [number, number, number] = [wB[0], 0, wB[1]];
      const nIA: [number, number, number] = [iA[0], 0, iA[1]];
      const nIB: [number, number, number] = [iB[0], 0, iB[1]];
      const UP: [number, number, number] = [0, 1, 0];

      // Front (water-side) face, welded along the ribbon via shared normals.
      addQuad(fab, fbb, fb, fa, nFA, nFB, nFB, nFA, FRONT_COLOR);

      // Thin dark band straddling the waterline, inset ~3 cm toward the
      // water so it sits proud of the front face and reads as a wet stain.
      const bandHi0 = clampNum(yB0, yT0, waterLevel + WATERLINE_BAND_HALF_M);
      const bandLo0 = clampNum(yB0, yT0, waterLevel - WATERLINE_BAND_HALF_M);
      const bandHi1 = clampNum(yB1, yT1, waterLevel + WATERLINE_BAND_HALF_M);
      const bandLo1 = clampNum(yB1, yT1, waterLevel - WATERLINE_BAND_HALF_M);
      const inset = 0.03;
      const wAlo: [number, number, number] = [
        a[0] + wA[0] * inset,
        bandLo0,
        a[1] + wA[1] * inset,
      ];
      const wAhi: [number, number, number] = [
        a[0] + wA[0] * inset,
        bandHi0,
        a[1] + wA[1] * inset,
      ];
      const wBlo: [number, number, number] = [
        b[0] + wB[0] * inset,
        bandLo1,
        b[1] + wB[1] * inset,
      ];
      const wBhi: [number, number, number] = [
        b[0] + wB[0] * inset,
        bandHi1,
        b[1] + wB[1] * inset,
      ];
      addQuad(wAlo, wBlo, wBhi, wAhi, nFA, nFB, nFB, nFA, WATERLINE_COLOR);

      // Top capstone, normal pointing up.
      addQuad(fa, fb, bb, ba, UP, UP, UP, UP, TOP_COLOR);

      // Back (inland) face. Often hidden under terrain but kept for the
      // case where the wall stands proud above the bank.
      addQuad(bbb, bab, ba, bb, nIA, nIB, nIB, nIA, SIDE_COLOR);

      // End caps for the first and last ribbon segments so the open ends
      // aren't visible.
      if (i === 0) {
        const capN: [number, number, number] = [
          -(b[0] - a[0]) / Math.max(1e-6, Math.hypot(b[0] - a[0], b[1] - a[1])),
          0,
          -(b[1] - a[1]) / Math.max(1e-6, Math.hypot(b[0] - a[0], b[1] - a[1])),
        ];
        addQuad(fab, fa, ba, bab, capN, capN, capN, capN, SIDE_COLOR);
      }
      if (i === n - 2) {
        const capN: [number, number, number] = [
          (b[0] - a[0]) / Math.max(1e-6, Math.hypot(b[0] - a[0], b[1] - a[1])),
          0,
          (b[1] - a[1]) / Math.max(1e-6, Math.hypot(b[0] - a[0], b[1] - a[1])),
        ];
        addQuad(fbb, bbb, bb, fb, capN, capN, capN, capN, SIDE_COLOR);
      }
    }
  }

  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label: GROUP_ID,
  };
}

function clampNum(lo: number, hi: number, v: number): number {
  if (lo > hi) return clampNum(hi, lo, v);
  return Math.max(lo, Math.min(hi, v));
}
